import React from 'react';
import { Formik } from 'formik';

import { useTranslation } from 'react-i18next';
import { ChargingPolicy } from '../../types';
import {
  Button,
  Container,
  Form,
  Grid,
  Header,
  Message,
  Segment,
  Divider,
  FormGroup,
} from 'semantic';
import { chargingPolicySchema } from '../../validation';
import { FeatureFlag } from 'components';
import InputField from 'components/form-fields/formik/InputField';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { useUser } from 'contexts/user';
import { TariffRules } from './TariffRules';
import PriceField from 'components/form-fields/formik/PriceField';
import ChargingPolicyAssociationSelection from 'subdomains/cpo/screens/GroupsAndPolicies/ChargingPolicies/form/ChargingPolicyAssociationSelection';

export const ChargingPolicyForm = () => {
  const { t } = useTranslation();

  const { provider } = useUser();

  const submit = async (values: ChargingPolicy) => {
    console.log('submit', values);
  };

  return (
    <FeatureFlag feature="policies_and_groups">
      <Container>
        <Formik
          initialValues={
            {
              label: 'test',
              currency: 'EUR',
              sessionThresholds: {},
              tariffRules: [
                {
                  __id: '1',
                  authorizationMethods: ['CHARGE_CARD', 'TAP_TO_PAY'],
                  customerGroups: ['CUSTOMER_GROUP_1'],
                  tariff: {
                    components: [
                      {
                        __id: '1',
                        price: {
                          type: 'FLAT',
                          value: 1,
                        },
                      },
                      {
                        __id: '2',
                        price: {
                          type: 'TIME',
                          value: 0.35,
                          unit: 'HOUR',
                        },
                        restrictions: {
                          enabled: true,
                          timeOfDay: {
                            enabled: true,
                            start: 7200,
                            end: 53304,
                          },
                          daysOfWeek: {
                            enabled: true,
                            values: ['monday', 'tuesday', 'saturday'],
                          },
                          date: {
                            enabled: true,
                            start: new Date('2024-01-01'),
                            end: new Date('2024-12-31'),
                          },
                          duration: {
                            enabled: true,
                            unit: 'MINUTE',
                            min: 200,
                            max: 500,
                          },
                          energy: {
                            enabled: true,
                            min: 5,
                          },
                        },
                      },
                    ],
                    descriptions: [
                      {
                        __id: 'desc1',
                        language: 'en',
                        text: 'Standard business hours tariff',
                      },
                    ],
                  },
                  validityPeriod: {
                    enabled: true,
                    start: new Date('2024-01-01'),
                    end: new Date('2024-12-31'),
                  },
                },
                {
                  __id: '2',
                  authorizationMethods: [],
                  customerGroups: [],
                  tariff: {
                    components: [
                      {
                        __id: '1',
                        price: {
                          type: 'FLAT',
                          value: 1,
                        },
                      },
                      {
                        __id: '2',
                        price: {
                          type: 'TIME',
                          value: 0.35,
                          unit: 'HOUR',
                        },
                      },
                    ],
                  },
                },
              ],
            } as ChargingPolicy
          }
          validationSchema={chargingPolicySchema}
          onSubmit={submit}>
          {({ values, status, handleSubmit, handleReset, setFieldValue }) => (
            <Form>
              <Header
                as="h1"
                content={t(
                  'chargingPolicyForm.createChargingPolicyTitle',
                  'Create Charging Policy'
                )}
              />

              {status && <Message error content={status} />}

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.generalInfoTitle',
                  'General Info'
                )}
              />
              <InputField
                name="label"
                label={t('chargingPolicyForm.label', 'Name')}
                required
              />

              <InputField
                name="description"
                label={t('chargingPolicyForm.description', 'Description')}
                required
              />

              <DropdownField
                name="currency"
                selection
                label={t('chargingPolicyForm.currency', 'Currency')}
                options={
                  provider?.billingCurrencies.map((currency) => ({
                    key: currency,
                    value: currency,
                    text: currency,
                  })) || []
                }
              />

              <Divider hidden />

              <Header
                as="h3"
                content={t('chargingPolicyForm.pricingTitle', 'Pricing')}
              />
              <Message
                info
                content={t(
                  'chargingPolicyForm.pricingDescription',
                  'Tariffs are evaluated based on descending order (from top to bottom). Make sure that more specific tariffs (e.g. with specific payment options or customer groups) are first in the list and general tariffs (e.g. all payment options, applies to everyone) last in the list.'
                )}
              />
              <TariffRules values={values?.tariffRules} />

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.sessionLimitsTitle',
                  'Session limits'
                )}
              />
              <FormGroup style={{ marginLeft: '0' }}>
                <PriceField
                  name="maxCost"
                  currency={values?.currency}
                  label={t('sessionThresholds.maxCost', 'Max session cost')}
                />
              </FormGroup>

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.sessionGracePeriodTitle',
                  'Grace period'
                )}
              />
              <FormGroup>
                <InputField
                  name="minTimeMinutes"
                  label={t('sessionThresholds.minTime', 'Min session time')}
                />
                <InputField
                  name="minKwhConsumed"
                  label={t(
                    'sessionThresholds.minEnergy',
                    'Min energy consumed'
                  )}
                />
              </FormGroup>

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.policyAssociationTitle',
                  'Policy association'
                )}
              />
              <ChargingPolicyAssociationSelection
                values={values?.associatedGroups}
                onChange={(values) => setFieldValue('associatedGroups', values)}
              />

              <Divider />

              <div style={{ float: 'right' }}>
                <Button
                  content={t('chargingPolicyForm.cancel', 'Discard')}
                  as="button"
                  basic
                  style={{ marginLeft: '0' }} // TODO: look for better solution
                  onClick={handleReset}
                />
                <Button
                  content={t(
                    'chargingPolicyForm.createPolicy',
                    'Create Policy'
                  )}
                  as="button"
                  style={{ marginLeft: '0' }} // TODO: look for better solution
                  onClick={handleSubmit}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Container>
    </FeatureFlag>
  );
};
